import React, { useState } from 'react'
import {
  Box,
  Heading,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  VStack,
  Button,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  ButtonGroup,
  Badge,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useGetVideos, useDeleteVideo } from 'queries/video.query'
import PropTypes from 'prop-types'
import { AiOutlineDelete } from 'react-icons/ai'
import { EditIcon } from '@chakra-ui/icons'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import Card from 'components/card/Card'
import { EditVideoModal } from './EditVideoModal'
import { DeleteVideoModal } from './DeleteVideoModal'

const columnHelper = createColumnHelper()

export default function VideoDashboard({ filterType }) {
  const { data: videos, isLoading, isError, error, refetch } = useGetVideos()
  const { mutate: deleteMutate, isPending: isDeleting } = useDeleteVideo()
  
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700')
  const toast = useToast()

  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()

  const [selectedItem, setSelectedItem] = useState(null)

  const filteredVideos = React.useMemo(() => {
    if (!videos) return []
    if (!filterType || filterType === 'all') return videos
    return videos.filter(v => v.type === filterType)
  }, [videos, filterType])

  const handleDelete = (id) => {
    deleteMutate(id, {
      onSuccess: () => {
        toast({
          title: 'Video deleted.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onDeleteClose()
      },
      onError: (err) => {
        toast({
          title: 'Error deleting video.',
          description: err.response?.data?.message || err.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    })
  }

  const columns = [
    columnHelper.accessor('title', {
      header: 'Title',
      cell: (info) => <Text fontWeight='semibold' color={textColor}>{info.getValue()}</Text>
    }),
    columnHelper.accessor('type', {
      header: 'Type',
      cell: (info) => (
        <Badge colorScheme={info.getValue() === 'youtube' ? 'red' : 'blue'}>
          {info.getValue()}
        </Badge>
      )
    }),
    columnHelper.accessor('link_or_file', {
      id: 'link_or_file',
      header: 'Source',
      cell: (info) => {
        const item = info.row.original;
        if (item.type === 'youtube') {
          return <a href={item.youtube_url} target="_blank" rel="noreferrer" style={{color:'blue', textDecoration:'underline'}}>Link</a>
        } else {
          return <Text fontSize="sm" color="gray.500">File Uploaded</Text>
        }
      }
    }),
    columnHelper.accessor('is_active', {
      header: 'Status',
      cell: (info) => (
        <Badge colorScheme={info.getValue() ? 'green' : 'red'}>
          {info.getValue() ? 'Active' : 'Inactive'}
        </Badge>
      )
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <ButtonGroup size="sm">
          <Button
            leftIcon={<EditIcon />}
            colorScheme="blue"
            variant="outline"
            onClick={() => {
              setSelectedItem(info.row.original)
              onEditOpen()
            }}
          >
            Edit
          </Button>
          <Button
            leftIcon={<AiOutlineDelete />}
            colorScheme="red"
            variant="outline"
            onClick={() => {
              setSelectedItem(info.row.original)
              onDeleteOpen()
            }}
          >
            Delete
          </Button>
        </ButtonGroup>
      )
    })
  ]

  const table = useReactTable({
    data: filteredVideos || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (isLoading) return <SkeletonTable />

  if (isError) {
    return (
      <Box p={5}>
        <Alert status='error' borderRadius='md'>
          <AlertIcon />
          <AlertTitle>Error loading videos!</AlertTitle>
          <AlertDescription>
            {error?.response?.data?.message || error.message}
          </AlertDescription>
          <Button mt={4} onClick={refetch} colorScheme='red' size='sm'>
            Retry
          </Button>
        </Alert>
      </Box>
    )
  }

  return (
    <Card p='0px' overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Box p='20px'>
        <Table variant='simple' color='gray.500' mb='24px' mt='12px'>
          <Thead bg={TheadBackground} position='sticky' top={0} zIndex={1}>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    borderColor={borderColor}
                    color='gray.400'
                    textTransform='capitalize'
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.length === 0 ? (
              <Tr>
                <Td colSpan={columns.length} textAlign='center' py={10}>
                  No videos found.
                </Td>
              </Tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Tr key={row.id} _hover={{ bg: hoverBackgroundColor }}>
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      borderColor={borderColor}
                      fontSize={{ sm: '14px' }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
      
      {selectedItem && (
        <EditVideoModal
          isOpen={isEditOpen}
          onClose={() => {
            onEditClose()
            setSelectedItem(null)
          }}
          video={selectedItem}
        />
      )}

      {selectedItem && (
        <DeleteVideoModal
          isOpen={isDeleteOpen}
          onClose={() => {
            onDeleteClose()
            setSelectedItem(null)
          }}
          video={selectedItem}
          onDelete={() => handleDelete(selectedItem.id)}
          isDeleting={isDeleting}
        />
      )}
    </Card>
  )
}

VideoDashboard.propTypes = {
  filterType: PropTypes.string,
}
